import { useAuthContext } from '@/features/auth/context'
import { EValveType } from '@/features/config/components/sidebar/HydraulicRule/FormValveView/constant'
import { useConfigUI } from '@/features/config/context'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { FlowValveType } from '@/types/hydraulic'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { FC } from 'react'
import classes from './style.module.scss'
import { is } from 'date-fns/locale'

interface FlowValveTableProps {
  rows: FlowValveType[]
  onDeleteItem?: (id: string, interfaceId: string) => void
  flowRuleId: number
}

const FlowValveTable: FC<FlowValveTableProps> = ({
  rows,
  onDeleteItem,
  flowRuleId,
}) => {
  const { t } = useTranslation()
  const { interfaceId } = useAuthContext()
  const { convertLabelUnit, convertFromMetricV2 } = useUnits()

  const { openSidebar, setSidebarView } = useConfigUI()
  const handleModalDelete = (id: string, interfaceId: string) => {
    onDeleteItem && onDeleteItem(id, interfaceId)
  }
  const { isViewOnly } = usePermission()

  const handleEditItem = (row: FlowValveType) => {
    if (isViewOnly) return
    openSidebar()
    setSidebarView({ type: 'FLOW_VALVE_VIEW', id: row.id.toString(), flowRuleId, data: row })
  }
  const hasTwiCustomName = rows.some(row => !!row.twiCustomName)

  // errorName is frequently empty on flow valve errors, so fall back to the
  // flowValveError_<code> translation, then to the raw code.
  const getErrorMessage = (row: FlowValveType): string => {
    const name = row.errorName?.trim()
    if (name) return name
    if (row.errorCode) {
      const key = `flowValveError_${row.errorCode}`
      const translated = t(key)
      if (translated && translated !== key) return translated
      return String(row.errorCode)
    }
    return '-'
  }

  return (
    <Stack flexGrow="1">
      <TableContainer
        component={Paper}
        elevation={0}
        className={classes.tableContainer}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell sx={{ width: '5%' }} className={classes.borderRight}>
                #
              </TableCell>
              <TableCell
                sx={{
                  width: '7%',
                }}
                className={classes.borderRight}
              >
                {t('commonLabelsStatus')}
              </TableCell>
              <TableCell
                sx={{
                  width: '9%',
                }}
                className={classes.borderRight}
              >
                {t('commonLabelsType')}
              </TableCell>
              <TableCell
                sx={{
                  width: '6%',
                }}
                className={classes.borderRight}
              >
                {t('formsLabelState')}
              </TableCell>
              <TableCell
                sx={{
                  width: '6%',
                }}
                className={classes.borderRight}
              >
                {t('formsLabelsPriority')}
              </TableCell>
              <TableCell
                sx={{
                  width: '240px',
                }}
                className={classes.borderRight}
              >
                IO
              </TableCell>
              <TableCell
                sx={{
                  width: '9%',
                }}
                className={classes.borderRight}
              >
                {t('flowSensor')}
              </TableCell>
              {hasTwiCustomName && 
                <TableCell
                  sx={{
                    width: '34%',
                  }}
                  className={classes.borderRight}
                >
                  Controller Name
                </TableCell>
              }

              <TableCell
                sx={{
                  width: '8%',
                }}
                className={classes.borderRight}
              >
                {t('formsLabelsStartDelay(sec)')}
              </TableCell>
              <TableCell
                sx={{
                  width: '8%',
                }}
                className={classes.borderRight}
              >
                {t('formsLabelsStopDelay(sec)')}
              </TableCell>
              <TableCell
                sx={{
                  width: '8%',
                }}
                className={classes.borderRight}
              >
                {t('formsLabelsCapacity')} ({convertLabelUnit('l/m')})
              </TableCell>
              <TableCell
                sx={{
                  width: '7%',
                }}
                className={classes.borderRight}
              >
                {t('commonStatusError')}
              </TableCell>
              <TableCell
                sx={{
                  width: '7%',
                }}
                className={classes.borderRight}
              >
                {t('errorCode')}
              </TableCell>

              <TableCell
                sx={{
                  width: '250px',
                }}
              >
                {t('commonTableHeaderAction')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              backgroundColor: 'common.white',
            }}
          >
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell
                  component="th"
                  scope="row"
                  className={classes.borderRight}
                >
                  {row.id}
                </TableCell>

                <TableCell className={classes.borderRight}>
                  {row.active ? (
                    <Stack className={classes.actived}>
                      {t('commonStatusActive')}
                    </Stack>
                  ) : (
                    <Stack className={classes.inActived}>
                      {t('commonStatusInactive')}
                    </Stack>
                  )}
                </TableCell>

                <TableCell className={classes.borderRight}>
                  {EValveType[row.type]}
                </TableCell>
                <TableCell className={classes.borderRight}>
                  {row.stateName}
                </TableCell>
                <TableCell className={classes.borderRight}>
                  {row.priority}
                </TableCell>
                <TableCell className={classes.borderRight}>
                  <Box color="text.main" className={classes.addressIO}>
                    {row.io} <br />
                    {`${row.address}-${row.index}`}
                  </Box>
                </TableCell>

                <TableCell className={classes.borderRight}>
                  {row.flowSensorName ?? t('flowSensorDashboard.noFlowSensor')}
                </TableCell>
                {hasTwiCustomName && 
                  <TableCell className={classes.borderRight}>
                    {row.twiCustomName || '-'}
                  </TableCell>
                }
                <TableCell className={classes.borderRight}>
                  {row.startDelay}
                </TableCell>
                <TableCell className={classes.borderRight}>
                  {row.stopDelay}
                </TableCell>
                <TableCell className={classes.borderRight}>
                  {convertFromMetricV2(row.capacity, 'l/m', 'gpm', 2)}
                </TableCell>
                <TableCell className={classes.borderRight}>
                  {getErrorMessage(row)}
                </TableCell>

                <TableCell className={classes.borderRight}>
                  {row.errorCode}
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing="10px">
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        color: isViewOnly ? 'black' : 'primary.main',
                        opacity: isViewOnly ? 0.2 : 1,
                      }}
                      onClick={() => handleEditItem(row)}
                    >
                      {t('actionEdit')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        color: isViewOnly ? 'black' : 'grey.600',
                        opacity: isViewOnly ? 0.2 : 1,
                      }}
                      onClick={() =>
                        handleModalDelete(row.id.toString(), interfaceId + '')
                      }
                    >
                      {t('actionDelete')}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default FlowValveTable
